import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ChevronLeft, ChevronRight, Plus, Play, Trash2, Move, Share2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Returns the LOCAL calendar date as YYYY-MM-DD. Using toISOString() here would
// shift the date by a day for any user outside UTC (e.g. Australia/Sydney is
// UTC+11, so local midnight becomes the previous day in UTC → schedules
// appeared on the wrong cell). Always derive from local getters.
function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function monthStart(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function monthEnd(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function weekStart(d) {
  const copy = new Date(d);
  const day = copy.getDay();
  copy.setDate(copy.getDate() - day);
  return copy;
}

export default function CalendarPage() {
  const nav = useNavigate();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [dayDialog, setDayDialog] = useState(null); // yyyy-mm-dd

  useEffect(() => { (async () => setWorkouts((await api.get("/workouts")).data))(); }, []);

  const range = useMemo(() => {
    if (view === "day") return { start: iso(cursor), end: iso(cursor) };
    if (view === "week") {
      const s = weekStart(cursor);
      const e = new Date(s); e.setDate(e.getDate() + 6);
      return { start: iso(s), end: iso(e) };
    }
    return { start: iso(monthStart(cursor)), end: iso(monthEnd(cursor)) };
  }, [view, cursor]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/schedule", { params: range });
      setSchedules(data);
    })();
  }, [range.start, range.end]);

  const byDate = useMemo(() => {
    const m = {};
    schedules.forEach((s) => {
      // Normalize the date key to YYYY-MM-DD. Some schedules in the wild were
      // stored as full ISO timestamps ("2026-05-16T00:00:00+00:00") instead of
      // plain dates, which made the week-view lookup miss them while day/month
      // happened to tolerate them (different per-view cell-vs-row matching).
      // Slicing the first 10 chars makes ALL views see the same key set.
      const key = (s.date || "").slice(0, 10);
      if (!key) return;
      if (!m[key]) m[key] = [];
      m[key].push(s);
    });
    return m;
  }, [schedules]);

  const navigatePrev = () => {
    const c = new Date(cursor);
    if (view === "day") c.setDate(c.getDate() - 1);
    else if (view === "week") c.setDate(c.getDate() - 7);
    else c.setMonth(c.getMonth() - 1);
    setCursor(c);
  };
  const navigateNext = () => {
    const c = new Date(cursor);
    if (view === "day") c.setDate(c.getDate() + 1);
    else if (view === "week") c.setDate(c.getDate() + 7);
    else c.setMonth(c.getMonth() + 1);
    setCursor(c);
  };

  const addToDate = async (date, workout_id) => {
    const { data } = await api.post("/schedule", { workout_id, date });
    setSchedules((s) => [...s, data]);
    setDayDialog(null);
    toast.success("Scheduled");
  };
  const remove = async (sid) => {
    await api.delete(`/schedule/${sid}`);
    setSchedules((s) => s.filter((x) => x.schedule_id !== sid));
  };
  const moveSchedule = async (sid, newDate) => {
    if (!sid || !newDate) return;
    const cur = schedules.find((x) => x.schedule_id === sid);
    if (cur && cur.date === newDate) return;
    setSchedules((s) => s.map((x) => x.schedule_id === sid ? { ...x, date: newDate } : x));
    try {
      await api.put(`/schedule/${sid}`, { date: newDate });
      toast.success(`Moved to ${newDate}`);
    } catch (e) {
      // revert on error
      if (cur) setSchedules((s) => s.map((x) => x.schedule_id === sid ? cur : x));
      toast.error(e.response?.data?.detail || "Could not move");
    }
  };
  const [moveDialog, setMoveDialog] = useState(null); // { schedule_id, current_date }
  const [shareDialog, setShareDialog] = useState(false);

  /**
   * Format the schedules currently in view as a plain-text summary suitable
   * for both chat send and the native share sheet. Stays readable in any
   * messaging app (no markdown, just emoji + line breaks).
   */
  const buildShareText = () => {
    const grouped = {};
    // Use ALL fetched schedules (i.e. exactly what's visible in this view's range)
    schedules.forEach((s) => {
      const k = (s.date || "").slice(0, 10);
      if (!k) return;
      (grouped[k] = grouped[k] || []).push(s.workout_name);
    });
    const dates = Object.keys(grouped).sort();
    const header =
      view === "day" ? `📅 My workout on ${title}`
      : view === "week" ? `📅 My gym week — ${title}`
      : `📅 My gym month — ${title}`;
    if (dates.length === 0) return `${header}\n\n(nothing scheduled yet)\n\n— via gymwithgrizz.app`;
    const lines = dates.map((d) => {
      const dt = new Date(`${d}T12:00:00`); // noon to avoid TZ rollover
      const label = dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
      return `• ${label}: ${grouped[d].join(", ")}`;
    });
    return `${header}\n\n${lines.join("\n")}\n\n— via gymwithgrizz.app 🏋️`;
  };

  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    const first = monthStart(cursor);
    const last = monthEnd(cursor);
    const startPad = first.getDay();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      const dt = new Date(cursor.getFullYear(), cursor.getMonth(), d);
      cells.push(dt);
    }
    return cells;
  }, [view, cursor]);

  const weekDays = useMemo(() => {
    if (view !== "week") return [];
    const s = weekStart(cursor);
    return Array.from({ length: 7 }, (_, i) => { const c = new Date(s); c.setDate(c.getDate() + i); return c; });
  }, [view, cursor]);

  const title = view === "month"
    ? cursor.toLocaleString(undefined, { month: "long", year: "numeric" })
    : view === "week"
    ? `Week of ${weekStart(cursor).toLocaleDateString()}`
    : cursor.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-1">Calendar</h1>
      <div className="text-xs tracking-[0.2em] uppercase text-neutral-400 mb-5">Plan your grind</div>

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none h-auto p-0">
          <TabsTrigger value="day" data-testid="view-day" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Day</TabsTrigger>
          <TabsTrigger value="week" data-testid="view-week" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Week</TabsTrigger>
          <TabsTrigger value="month" data-testid="view-month" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Month</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-between mb-4">
        <Button size="icon" variant="outline" onClick={navigatePrev} data-testid="cal-prev" className="bg-transparent border-neutral-700 text-white rounded-none"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="font-display text-2xl uppercase">{title}</div>
        <Button size="icon" variant="outline" onClick={navigateNext} data-testid="cal-next" className="bg-transparent border-neutral-700 text-white rounded-none"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Share / save current view */}
      <div className="flex justify-end mb-3">
        <Button
          size="sm"
          variant="outline"
          data-testid="cal-share-btn"
          onClick={() => setShareDialog(true)}
          className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-8"
        >
          <Share2 className="w-3 h-3 mr-1.5" /> Save & Share
        </Button>
      </div>

      {view === "day" && <DayList date={iso(cursor)} items={byDate[iso(cursor)] || []} onStart={(wid) => nav(`/active/${wid}`)} onAdd={() => setDayDialog(iso(cursor))} onRemove={remove} onMove={(s) => setMoveDialog(s)} onDropMove={moveSchedule} />}

      {view === "week" && (
        <div className="space-y-2">
          {weekDays.map((d) => (
            <DayList key={iso(d)} date={iso(d)} items={byDate[iso(d)] || []} onStart={(wid) => nav(`/active/${wid}`)} onAdd={() => setDayDialog(iso(d))} onRemove={remove} onMove={(s) => setMoveDialog(s)} onDropMove={moveSchedule} />
          ))}
        </div>
      )}

      {view === "month" && (
        <div className="grid grid-cols-7 gap-1 border border-neutral-800 p-1">
          {["S","M","T","W","T","F","S"].map((d, i) => (
            <div key={`dh-${i}`} className="text-center py-2 text-xs uppercase tracking-widest text-neutral-500 font-bold">{d}</div>
          ))}
          {monthCells.map((d, i) => d ? (
            <button
              key={iso(d)}
              data-testid={`month-cell-${iso(d)}`}
              onClick={() => setDayDialog(iso(d))}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2","ring-blue-500"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("ring-2","ring-blue-500")}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("ring-2","ring-blue-500");
                const sid = e.dataTransfer.getData("text/schedule-id");
                if (sid) moveSchedule(sid, iso(d));
              }}
              className={`aspect-square p-1 text-left border ${
                byDate[iso(d)]?.length ? "border-blue-500 bg-blue-500/10" : "border-neutral-800 hover:border-neutral-600"
              }`}
            >
              <div className="text-xs font-semibold">{d.getDate()}</div>
              {byDate[iso(d)]?.length > 0 && (
                <div className="space-y-0.5 mt-1">
                  {byDate[iso(d)].slice(0, 2).map((s) => (
                    <div
                      key={s.schedule_id}
                      data-testid={`month-chip-${s.schedule_id}`}
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("text/schedule-id", s.schedule_id); e.dataTransfer.effectAllowed = "move"; }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[9px] text-blue-400 truncate uppercase tracking-wider cursor-grab active:cursor-grabbing border border-blue-500/40 px-1"
                      title={`Drag to move "${s.workout_name}"`}
                    >
                      {s.workout_name}
                    </div>
                  ))}
                  {byDate[iso(d)].length > 2 && <div className="text-[9px] text-neutral-500">+{byDate[iso(d)].length - 2} more</div>}
                </div>
              )}
            </button>
          ) : <div key={`empty-${i}`} />)}
        </div>
      )}

      <Dialog open={!!dayDialog} onOpenChange={(o) => !o && setDayDialog(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Add to {dayDialog}</DialogTitle></DialogHeader>
          {workouts.length === 0 ? (
            <div className="text-neutral-400 text-sm">Create a workout first in the Library.</div>
          ) : (
            <div className="divide-y divide-neutral-800 border border-neutral-800 max-h-96 overflow-y-auto">
              {workouts.map((w) => (
                <button key={w.workout_id} data-testid={`sched-wk-${w.workout_id}`} onClick={() => addToDate(dayDialog, w.workout_id)} className="w-full flex items-center justify-between p-3 hover:bg-neutral-900 text-left">
                  <div>
                    <div className="font-display text-lg uppercase">{w.name}</div>
                    <div className="text-xs text-neutral-500 uppercase">{w.exercises?.length || 0} exercises</div>
                  </div>
                  <Plus className="w-4 h-4 text-blue-500" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!moveDialog} onOpenChange={(o) => !o && setMoveDialog(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md">
          <DialogHeader><DialogTitle data-testid="move-dialog-title" className="font-display text-2xl uppercase">Move "{moveDialog?.workout_name}"</DialogTitle></DialogHeader>
          <div className="text-xs text-neutral-400">Currently on {moveDialog?.date}. Pick a new date.</div>
          <input
            type="date"
            data-testid="move-date-input"
            defaultValue={moveDialog?.date}
            onChange={(e) => setMoveDialog((m) => m ? { ...m, _newDate: e.target.value } : m)}
            className="bg-neutral-900 border border-neutral-800 rounded-none h-10 px-3 text-white"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setMoveDialog(null)} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-9">Cancel</Button>
            <Button
              data-testid="move-confirm-btn"
              onClick={async () => {
                const newDate = moveDialog?._newDate || moveDialog?.date;
                if (newDate && newDate !== moveDialog?.date) {
                  await moveSchedule(moveDialog.schedule_id, newDate);
                }
                setMoveDialog(null);
              }}
              disabled={!moveDialog?._newDate || moveDialog?._newDate === moveDialog?.date}
              className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9"
            >Move</Button>
          </div>
        </DialogContent>
      </Dialog>

      <CalendarShareDialog
        open={shareDialog}
        onClose={() => setShareDialog(false)}
        text={buildShareText()}
        viewLabel={title}
      />
    </div>
  );
}

function DayList({ date, items, onStart, onAdd, onRemove, onMove, onDropMove }) {
  const d = new Date(date);
  return (
    <div
      data-testid={`day-${date}`}
      className="border border-neutral-800"
      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-blue-500"); }}
      onDragLeave={(e) => e.currentTarget.classList.remove("border-blue-500")}
      onDrop={(e) => {
        e.preventDefault();
        e.currentTarget.classList.remove("border-blue-500");
        const sid = e.dataTransfer.getData("text/schedule-id");
        if (sid) onDropMove?.(sid, date);
      }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-800 bg-neutral-900/60">
        <div className="text-sm font-display uppercase tracking-wide">
          {d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
        </div>
        <Button size="sm" onClick={onAdd} data-testid={`add-to-day-${date}`} variant="ghost" className="text-blue-500 hover:text-blue-400 uppercase tracking-wider font-display h-7">
          <Plus className="w-3 h-3 mr-1" /> Plan
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-3 text-xs text-neutral-500">Nothing scheduled.</div>
      ) : (
        items.map((s) => (
          <div
            key={s.schedule_id}
            draggable
            onDragStart={(e) => { e.dataTransfer.setData("text/schedule-id", s.schedule_id); e.dataTransfer.effectAllowed = "move"; }}
            className="flex items-center justify-between px-3 py-3 border-t border-neutral-900 first:border-t-0 cursor-grab active:cursor-grabbing"
          >
            <div>
              <div className="font-semibold">{s.workout_name}</div>
              <div className="text-xs text-neutral-500 uppercase">{s.status}</div>
            </div>
            <div className="flex gap-1.5">
              <Button size="icon" onClick={() => onMove?.(s)} variant="ghost" data-testid={`move-${s.schedule_id}`} className="text-neutral-400 hover:text-blue-500 h-8 w-8" title="Move to another day">
                <Move className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" onClick={() => onStart(s.workout_id)} data-testid={`start-${s.schedule_id}`} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">
                <Play className="w-3 h-3 mr-1" /> Start
              </Button>
              <Button size="icon" onClick={() => onRemove(s.schedule_id)} variant="ghost" className="text-neutral-400 hover:text-blue-500 h-8 w-8">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}


/**
 * Save & Share dialog for the calendar.
 *  - Send to a gym buddy → posts the summary as a chat message
 *  - Copy to clipboard
 *  - Native share sheet (Web Share API where available)
 */
function CalendarShareDialog({ open, onClose, text, viewLabel }) {
  const { user } = useAuth();
  const meId = user?.user_id || null;
  const [buddies, setBuddies] = useState([]);
  const [buddiesLoading, setBuddiesLoading] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const [sentTo, setSentTo] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setSentTo(new Set());
    let cancelled = false;
    (async () => {
      setBuddiesLoading(true);
      try {
        const { data } = await api.get("/buddies");
        if (!cancelled) setBuddies(Array.isArray(data?.accepted) ? data.accepted : []);
      } finally { if (!cancelled) setBuddiesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const otherSideOf = (b) => {
    if (meId && b.from_user_id === meId) return { id: b.to_user_id, name: b.to_name };
    return { id: b.from_user_id, name: b.from_name };
  };

  const sendToBuddy = async (b) => {
    const { id: targetId, name } = otherSideOf(b);
    if (!targetId) { toast.error("Couldn't resolve buddy"); return; }
    setSendingTo(targetId);
    try {
      await api.post("/chat/send", { to_user_id: targetId, text });
      setSentTo((prev) => { const n = new Set(prev); n.add(targetId); return n; });
      toast.success(`Sent to ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setSendingTo(null); }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyToClipboard(); return; }
    try { await navigator.share({ title: `My gym plan — ${viewLabel}`, text }); }
    catch { /* user cancelled */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">Save & Share</DialogTitle>
        </DialogHeader>

        <pre data-testid="share-preview" className="bg-neutral-900 border border-neutral-800 p-3 text-xs text-neutral-300 whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">{text}</pre>

        <div className="flex gap-2">
          <Button
            data-testid="share-native-btn"
            onClick={nativeShare}
            className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10"
          >
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> {typeof navigator !== "undefined" && navigator.share ? "Share via app" : "Copy"}
          </Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button
              data-testid="share-copy-btn"
              variant="outline"
              onClick={copyToClipboard}
              className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-10"
            >
              Copy
            </Button>
          )}
        </div>

        <div className="text-xs uppercase tracking-widest text-neutral-400 mt-2 mb-1">Send to a gym buddy / trainer</div>
        {buddiesLoading ? (
          <div className="text-neutral-500 text-sm flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
        ) : buddies.length === 0 ? (
          <div className="border border-dashed border-neutral-800 p-4 text-xs text-neutral-400 text-center">
            No gym buddies yet. Invite someone from the Squad tab first.
          </div>
        ) : (
          <div className="divide-y divide-neutral-800 border border-neutral-800 max-h-56 overflow-y-auto">
            {buddies.map((b) => {
              const { id: otherId, name } = otherSideOf(b);
              const sent = sentTo.has(otherId);
              const sending = sendingTo === otherId;
              return (
                <div key={b.request_id} className="p-2.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 bg-blue-500 flex items-center justify-center font-display text-sm shrink-0">{name?.[0]?.toUpperCase() || "?"}</div>
                    <div className="font-semibold truncate">{name}</div>
                  </div>
                  <Button
                    size="sm"
                    data-testid={`share-buddy-${b.request_id}`}
                    onClick={() => sendToBuddy(b)}
                    disabled={sending || sent}
                    className={
                      sent
                        ? "bg-transparent border border-green-500/60 text-green-400 rounded-none uppercase tracking-wider font-display h-8 shrink-0"
                        : "bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8 shrink-0"
                    }
                  >
                    {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : sent ? <><Check className="w-3 h-3 mr-1" /> Sent</> : "Send"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
